import {
  type IconDefinition,
  faCircleQuestion,
  faLightbulb,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { ErrorAlert, StyledDropzone } from "@sweco-se/reality-frontend";
import { type FC, useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";

import { BACKEND_URL } from "@/api.ts";
import { MAX_ZAMMAD_ATTACHMENT_BYTES } from "@/constants.ts";

type TicketType = "feature_request" | "incident" | "question";

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // `result` is a data URL ("data:<mime>;base64,<data>"); we only want
      // the base64 payload.
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export const CreateZammadTicketModal: FC = () => {
  const { t } = useLingui();
  const auth = useAuth();

  const [attachment, setAttachment] = useState<File | undefined>();
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const [createdTicketNumber, setCreatedTicketNumber] = useState<
    string | undefined
  >();

  const form = useForm({
    initialValues: {
      type: "question" as TicketType,
      title: "",
      body: "",
    },
    validate: {
      title: (value) => (value.trim().length > 0 ? null : t`Title is required`),
      body: (value) => (value.trim().length > 0 ? null : t`Body is required`),
    },
  });

  const ticketTypeOptions: Array<{
    value: TicketType;
    label: string;
    description: string;
    icon: IconDefinition;
  }> = [
    {
      value: "question",
      label: t({ message: "Question", context: "ticket type" }),
      description: t({
        message: "A general question",
        context: "ticket type description",
      }),
      icon: faCircleQuestion,
    },
    {
      value: "incident",
      label: t({ message: "Incident", context: "ticket type" }),
      description: t({
        message: "An unexpected issue, problem, bug, or similar",
        context: "ticket type description",
      }),
      icon: faTriangleExclamation,
    },
    {
      value: "feature_request",
      label: t({ message: "Feature request", context: "ticket type" }),
      description: t({
        message: "A request for a new feature, enhancement, data, etc.",
        context: "ticket type description",
      }),
      icon: faLightbulb,
    },
  ];

  const handleSubmit = useCallback<Parameters<(typeof form)["onSubmit"]>[0]>(
    async (values) => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/external/zammad/tickets`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${auth.user?.access_token}`,
            },
            body: JSON.stringify({
              title: values.title,
              body: values.body,
              type: values.type,
              attachment: attachment
                ? {
                    filename: attachment.name,
                    mime_type: attachment.type || "application/octet-stream",
                    content_base64: await fileToBase64(attachment),
                  }
                : null,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(
            t`Failed to create ticket (status ${response.status})`,
          );
        }

        const result: { number: string } = await response.json();
        setCreatedTicketNumber(result.number);
      } catch (e) {
        form.setFieldError(
          "__root",
          e instanceof Error ? e.message : t`Failed to create ticket`,
        );
      }
    },
    [auth.user?.access_token, attachment, form, t],
  );

  if (createdTicketNumber) {
    return (
      <Stack mt="md">
        <Text>
          <Trans>
            Your ticket #{createdTicketNumber} has been created. Our support
            team will get back to you by email.
          </Trans>
        </Text>
        <Group justify="flex-end">
          <Button onClick={() => modals.closeAll()}>
            <Trans>Close</Trans>
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack mt="md">
        <Select
          label={t({ message: "Type", context: "ticket" })}
          withAsterisk
          allowDeselect={false}
          disabled={form.submitting}
          data={ticketTypeOptions}
          key={form.key("type")}
          description={
            ticketTypeOptions.find((o) => o.value === form.values.type)
              ?.description
          }
          inputWrapperOrder={["label", "input", "description", "error"]}
          leftSection={
            <FontAwesomeIcon
              icon={
                ticketTypeOptions.find((o) => o.value === form.values.type)
                  ?.icon ?? faCircleQuestion
              }
            />
          }
          renderOption={({ option }) => {
            const ticketType = ticketTypeOptions.find(
              (o) => o.value === option.value,
            );
            return (
              <Group gap="sm" wrap="nowrap">
                <FontAwesomeIcon icon={ticketType?.icon ?? faCircleQuestion} />
                <Text size="sm">{option.label}</Text>
              </Group>
            );
          }}
          {...form.getInputProps("type")}
        />
        <TextInput
          label={t`Title`}
          withAsterisk
          disabled={form.submitting}
          key={form.key("title")}
          {...form.getInputProps("title")}
        />
        <Textarea
          label={t`Description`}
          withAsterisk
          autosize
          minRows={4}
          disabled={form.submitting}
          key={form.key("body")}
          {...form.getInputProps("body")}
        />
        <Stack gap="xs">
          {attachment ? (
            <Group justify="space-between">
              <Text size="sm">{attachment.name}</Text>
              <Button
                variant="subtle"
                size="xs"
                disabled={form.submitting}
                onClick={() => {
                  setAttachment(undefined);
                  setAttachmentError(undefined);
                }}
              >
                <Trans>Remove</Trans>
              </Button>
            </Group>
          ) : (
            <StyledDropzone
              onDrop={(files) => {
                const file = files[0];
                if (file.size > MAX_ZAMMAD_ATTACHMENT_BYTES) {
                  setAttachmentError(t`The attachment must be at most 10 MB.`);
                  return;
                }
                setAttachmentError(undefined);
                setAttachment(file);
              }}
              onReject={() =>
                setAttachmentError(t`The attachment must be at most 10 MB.`)
              }
              maxFiles={1}
              maxSize={MAX_ZAMMAD_ATTACHMENT_BYTES}
              disabled={form.submitting}
              title={t`Click to select a file`}
              subtitle={t`Optional attachment, max 10 MB`}
              size="sm"
            />
          )}
          {attachmentError && <ErrorAlert>{attachmentError}</ErrorAlert>}
        </Stack>
        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            loading={form.submitting}
            disabled={form.submitting}
            color="green"
          >
            <Trans>Create support ticket</Trans>
          </Button>
        </Group>
        {form.errors.__root && <ErrorAlert>{form.errors.__root}</ErrorAlert>}
      </Stack>
    </form>
  );
};

export function openSupportTicketModal() {
  modals.open({
    title: globalT`Create support ticket`,
    children: <CreateZammadTicketModal />,
  });
}
